import React, { useState, useCallback, useId, useEffect, useMemo } from 'react';
import { FaRegThumbsDown, FaRegThumbsUp, FaTimes, FaTrash } from 'react-icons/fa';
import { ChatMessage } from '../types';
import { MarkdownMessage } from './MarkdownMessage';
import { CustomMessage } from './CustomMessage';
import { MessageContent } from './MessageContent';
import { CopyMarkdownButton } from './CopyMarkdownButton';
import { FeedbackModal } from './FeedbackModal';
import { PdfExportButton } from './PdfExportButton';
import { SavePromptFromResponseButton } from './SavePromptFromResponseButton';
import { logger } from '../utils/logger';
import { useFormatters } from '../context/LocalizationContext';
import { useAuth } from '../hooks/useAuth';
import { useMessageFeedback } from '../hooks/useMessageFeedback';
import { useTranslation } from '../localization/useAppTranslation';
import {
  createMapLabelLocalizer,
  extractMapDisplayDataFromEvents,
} from '../utils/mapToolRegistry';
import { pairHistoryAssistantResponsesWithSourcePrompts } from '../utils/promptSourcePairing';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import '../styles/SessionDialog.css';

const moduleLogger = logger.getModule('SessionDialog');

const LazyMapDisplay = React.lazy(() =>
  import('./MapDisplay').then((mod) => ({ default: mod.MapDisplay }))
);
const BOT_AVATAR_SRC = '/avatars/bot-avatar.svg';
const USER_AVATAR_SRC = '/avatars/user-solid-blue.svg';

interface SessionDialogProps {
  isOpen: boolean;
  sessionId: string;
  currentSessionId: string; // Current active session for link tracking
  description: string;
  startedAt: string;
  messages: ChatMessage[];
  onClose: () => void;
  onDelete: (sessionId: string) => void;
  onGenerateSavedPromptDraft?: (sourcePrompt: SavedPromptFromChatRequest) => Promise<void>;
}

export const SessionDialog: React.FC<SessionDialogProps> = ({
  isOpen,
  sessionId,
  currentSessionId,
  description,
  startedAt,
  messages,
  onClose,
  onDelete,
  onGenerateSavedPromptDraft,
}) => {
  const { t } = useTranslation('chat');
  const { t: tReports } = useTranslation('reports');
  const { formatDate } = useFormatters();
  const titleId = useId();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const {
    feedbackModal,
    openFeedback,
    closeFeedback,
    submitFeedback,
  } = useMessageFeedback({
    activeSessionId: currentSessionId,
    targetSessionId: sessionId,
  });
  const { authState, session, selectedFleetId, selectedFleetName, customerName } = useAuth();
  const sourcePromptsByAssistantId = useMemo(
    () => pairHistoryAssistantResponsesWithSourcePrompts(messages),
    [messages],
  );

  useEffect(() => {
    if (isOpen) {
      setShowDeleteConfirm(false);
      setIsDeleting(false);
      setDeleteError(null);
      closeFeedback();
    }
  }, [closeFeedback, isOpen, sessionId]);

  const formatSessionDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.valueOf())) {
        throw new Error('Invalid date');
      }
      const now = new Date();
      const currentYear = now.getFullYear();
      const dateYear = date.getFullYear();
      return formatDate(date, dateYear === currentYear
        ? {
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }
        : {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
    } catch (error) {
      moduleLogger.error('Failed to format datetime', { dateString, error });
      return t('history.invalidDate');
    }
  };

  const handleDelete = useCallback(async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError(null);
      moduleLogger.debug('Deleting session', { sessionId });
      await onDelete(sessionId);
      onClose();
    } catch (error) {
      moduleLogger.error('Failed to delete session', { sessionId, error });
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteError(t('history.deleteFailed'));
    }
  }, [sessionId, showDeleteConfirm, onDelete, onClose]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteError(null);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="session-dialog-overlay" onClick={onClose}>
        <div
          className="session-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Title bar */}
        <div className="session-dialog-header">
          <div className="session-dialog-info">
            <h2 id={titleId} className="session-dialog-title" title={description}>
              {description}
            </h2>
            <div className="session-dialog-date">{formatSessionDateTime(startedAt)}</div>
          </div>
          <div className="session-dialog-actions">
            {showDeleteConfirm ? (
              <>
                <button
                  type="button"
                  className="session-delete-confirm-btn"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  aria-label={t('history.confirmDeleteTitle')}
                  title={t('history.confirmDeleteTitle')}
                >
                  {t('history.confirmDelete')}
                </button>
                <button
                  type="button"
                  className="session-delete-cancel-btn"
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  aria-label={t('history.cancelDelete')}
                  title={t('history.cancelDelete')}
                >
                  {t('history.cancelDelete')}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="session-delete-btn"
                onClick={handleDelete}
                aria-label={t('history.delete')}
                title={t('history.deleteTitle')}
              >
                <FaTrash aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="session-close-btn"
              onClick={onClose}
              aria-label={t('history.closeTitle')}
              title={t('history.closeTitle')}
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        </div>
        {deleteError && <div className="session-dialog-error" role="alert">{deleteError}</div>}

        {/* Messages display */}
        <div className="session-dialog-body">
          <div className="session-message-list">
            {messages.map((msg) => {
              const streamEvents: Array<Record<string, any>> = Array.isArray(msg.metadata?.streamEvents)
                ? msg.metadata.streamEvents
                : [];
              const mapDisplayData = extractMapDisplayDataFromEvents(
                streamEvents,
                createMapLabelLocalizer(tReports),
              );
              const mapData = mapDisplayData
                ? {
                    center: mapDisplayData.center,
                    zoom: mapDisplayData.zoom,
                    markers: mapDisplayData.markers,
                  }
                : null;
              const sourcePrompt = sourcePromptsByAssistantId.get(msg.id);
              const canSaveAsPrompt =
                msg.role === 'assistant' &&
                Boolean(sourcePrompt?.source_prompt) &&
                Boolean(onGenerateSavedPromptDraft) &&
                Boolean(msg.content.trim()) &&
                !Boolean(msg.metadata?.syntheticMessageKey) &&
                !String(msg.id).startsWith('error-');
              const canSubmitFeedback =
                msg.role === 'assistant' &&
                Number.isInteger(msg.messageId) &&
                Boolean(msg.messageId && msg.messageId > 0);

              return (
              <CustomMessage
                key={msg.id}
                id={msg.id}
                direction={msg.role === 'user' ? 'outgoing' : 'incoming'}
                sender={msg.sender}
                content={msg.content}
                timestamp={typeof msg.timestamp === 'string' ? msg.timestamp : msg.timestamp?.toISOString()}
                avatarSrc={
                  msg.role === 'assistant'
                    ? BOT_AVATAR_SRC
                    : USER_AVATAR_SRC
                }
                avatarAlt={
                  msg.role === 'assistant' ? 'SpeedGauge' : msg.sender
                }
                showAvatar={true}
              >
                {msg.role === 'assistant' ? (
                  <MessageContent content="">
                    <div className="bot-message-container" data-message-id={msg.id}>
                      <MarkdownMessage
                        content={msg.content}
                        className="copilot-response"
                        messageId={msg.id}
                      />
                      {mapData && (
                        <React.Suspense fallback={<div className="map-loading">{t('map.loading')}</div>}>
                          <LazyMapDisplay
                            center={mapData.center}
                            zoom={mapData.zoom}
                            markers={mapData.markers}
                          />
                        </React.Suspense>
                      )}
                      <div className="message-footer message-actions">
                        {canSubmitFeedback && (
                          <div className="message-feedback-buttons">
                            <button
                              type="button"
                              className="message-action-button feedback-btn-inline"
                              onClick={() => openFeedback(msg.messageId!, 1)}
                              aria-label={t('feedback.helpfulAriaLabel')}
                              title={t('feedback.helpfulTitle')}
                            >
                              <FaRegThumbsUp aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="message-action-button feedback-btn-inline"
                              onClick={() => openFeedback(msg.messageId!, 0)}
                              aria-label={t('feedback.notHelpfulAriaLabel')}
                              title={t('feedback.notHelpfulTitle')}
                            >
                              <FaRegThumbsDown aria-hidden="true" />
                            </button>
                          </div>
                        )}
                        <PdfExportButton
                          showLabel={false}
                          className="message-action-button"
                          getExportData={() => ({
                            content: msg.content,
                            citations: msg.citations,
                            messageId: msg.id,
                            sessionId,
                            timestamp: msg.timestamp instanceof Date
                              ? msg.timestamp.toISOString()
                              : String(msg.timestamp),
                            userName: authState.user?.username,
                            customerId: session?.customerId || authState.user?.customerId,
                            customerName: customerName || session?.customerName,
                            vehicleGroupId: selectedFleetId,
                            vehicleGroupName: selectedFleetName,
                            mapData,
                            mapTitle: mapDisplayData?.title,
                          })}
                        />
                        {canSaveAsPrompt && sourcePrompt && onGenerateSavedPromptDraft && (
                          <SavePromptFromResponseButton
                            sourcePrompt={sourcePrompt}
                            onGenerateDraft={onGenerateSavedPromptDraft}
                          />
                        )}
                        <CopyMarkdownButton
                          content={msg.content}
                          showLabel={false}
                          className="message-action-button"
                        />
                      </div>
                    </div>
                  </MessageContent>
                ) : (
                  <MessageContent content={msg.content} />
                )}
              </CustomMessage>
              );
            })}
          </div>
        </div>
      </div>
      </div>
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedback}
        onSubmit={submitFeedback}
        initialRating={feedbackModal.initialRating}
      />
    </>
  );
};
